import { useState, ChangeEvent, FC, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import Modal from '@components/Modal';
import { GlobeContext } from '@providers/Globe';
import { CountriesContext } from '@providers/Countries';
import { ChatBotContext } from '@providers/ChatBot';
import { getToken } from '@helpers/utils';
import axios, { CancelTokenSource } from 'axios';
import AIMinisterIcon from '@assets/aiminister-icon.png';
import AIMinisterIconTop from '@assets/aiminister-icon-top.png';
import { FaTimes, FaUser } from 'react-icons/fa';
import { Button, IconButton, TextField } from '@mui/material';
// import LoaderImage from '@assets/loader.gif';
import PlotlyChart from './PlotlyChart';
import { ContentCopy, ThumbDown, ThumbUp, Translate, Warning, Send, Stop } from '@mui/icons-material';
import Typewriter from './Typewriter';
import { getTranslation } from './getTranslation';
import ChatMenu, { ChatMenuItemProps, ForecastSimulationItem, LaborDemandDataItem } from './ChatMenu';
import * as url from '@api/requests';
import { v4 as uuidv4 } from 'uuid';

type ChatModalProps = {
  isOpen: boolean;
  onClose: () => void
};
interface RequestSearchType {
  text: string;
  generate_plot: string;
}
interface InlineStylesType {
  containerHeight: string;
}
interface questionsInfoType {
  question: string;
  title: string;
  subtitle: string;
  generateGraph: boolean;
}

const questionsInfo: questionsInfoType[] = [
  {
    "question": "What are the most in-demand skills across all job categories?",
    "title": "In-Demand Skills",
    "subtitle": "Top Skills Required Across Various Job Categories",
    "generateGraph": false
  },
  {
    "question": "Which occupations have the highest demand for advanced degrees or specialized certifications?",
    "title": "Demand for Advanced Degrees and Certifications",
    "subtitle": "Occupations Requiring Advanced Qualifications",
    "generateGraph": false
  },
  {
    "question": "Which industries are experiencing a decline in job demand?",
    "title": "Declining Job Demand by Industry",
    "subtitle": "Industries with Decreasing Job Opportunities",
    "generateGraph": false
  },
  {
    "question": "What are the top 10 highest-paying jobs currently in demand?",
    "title": "Highest-Paying Jobs in Demand",
    "subtitle": "Top 10 Lucrative Occupations",
    "generateGraph": false
  },
  {
    "question": "What are the top 10 jobs with the highest demand for recent graduates or entry-level candidates?",
    "title": "Jobs in Demand for Recent Graduates",
    "subtitle": "Top 10 Entry-Level Opportunities",
    "generateGraph": false
  },
  {
    "question": "What are the top occupations with the highest job growth over the next 10 years?",
    "title": "Top Occupations with Highest Job Growth",
    "subtitle": "Occupations Expected to Grow the Most in 10 Years",
    "generateGraph": false
  }
]

const ChatModal: FC<ChatModalProps> = ({ isOpen, onClose }) => {
  let token = getToken();
  const [requestSearch, setRequestSearch] = useState<RequestSearchType>({ text: "", generate_plot: "true" });
  const [submitChatFunction, setSubmitChatFunction] = useState<number>(0);
  const [selectedChatMenuItemId, setSelectedChatMenuItemId] = useState<string>(LaborDemandDataItem);
  
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { selectedCountry } = useContext(GlobeContext);
  const { countryDetails } = useContext(CountriesContext);
  const [enableSendBtn, setEnableSendBtn] = useState<boolean>(true);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const { conversationId, setConversationId, tempRequestSearch, setTempRequestSearch } = useContext(ChatBotContext);
  const [inlineStyles, setInlineStyles] = useState<InlineStylesType>({ containerHeight: "calc(100vh - 270px)" });
  const [loadingResponse, setLoadingResponse] = useState<boolean>(false);

    const [isSimulationCreated, setIsSimulationCreated] = useState<boolean>(false);
    const [addToConversationToken, setAddToConversationToken] = useState<CancelTokenSource>();
    const [generateScenarioToken, setGenerateScenarioToken] = useState<CancelTokenSource>();
    const [getPlotsResultsToken, setGetPlotsResultsToken] = useState<CancelTokenSource>();
    
  const countryData = useMemo(() => {
    return (
      countryDetails?.find(({ code }: any) => code === selectedCountry) || null
    );
  }, [selectedCountry, countryDetails]);

  const handleChangeRequestSearch = (htmlinput: ChangeEvent<HTMLInputElement>) => {
    const newText = htmlinput.target.value
    setRequestSearch(prevState => ({
      ...prevState,
      text: newText
    }));
  }

  const handleKeyDownOnSearch = async (e: { key: string; preventDefault: () => void; }) => {
    if (e.key === 'Enter' && enableSendBtn) {
      await e.preventDefault(); handleSubmitSearch();
    }
  }

  const handleSuggestedBoxes = async (question: string, isChecked: boolean = false) => {
    setSelectedChatMenuItemId(LaborDemandDataItem);
    setRequestSearch({
      text: question,
      generate_plot: "true"
    });
    setLoadingResponse(true);
    setTempRequestSearch && setTempRequestSearch((prevTempRequestSearch) => [
      ...prevTempRequestSearch,
      {
        name: "You",
        placeholder: null,
        loading: false,
        text: question,
        lang: "en",
        showIcons: false,
        chart: null,
        stopConversation: false
      },
      {
        name: "Whiteshield",
        placeholder: null,
        loading: true,
        text: "",
        lang: "en",
        showIcons: false,
        chart: null,
        stopConversation: false
      }
    ]);
    setSubmitChatFunction((n: number) => n + 1)
  }

  const handleSubmitSearch = () => {
    if (!loadingResponse) {
      setLoadingResponse(true);
      setTempRequestSearch && setTempRequestSearch((prevTempRequestSearch) => [
        ...prevTempRequestSearch,
        {
          name: "You",
          placeholder: null,
          loading: false,
          text: requestSearch.text,
          lang: "en",
          showIcons: false,
          chart: null,
          stopConversation: false
        },
        {
          name: "Whiteshield",
          placeholder: null,
          loading: true,
          text: "",
          lang: "en",
          showIcons: false,
          chart: null,
          stopConversation: false
        }
      ]);
      setSubmitChatFunction((n: number) => n + 1)
    }
  };
  useEffect(() => {
    if (submitChatFunction > 0) {
      SubmitChat();
    }
  }, [submitChatFunction]);

    const handleNewConversation = useCallback(() => {
        setConversationId && setConversationId(uuidv4());
        setTempRequestSearch && setTempRequestSearch([
            {
                name: "Whiteshield",
                placeholder: null,
                loading: false,
                text: "Hello! I'm the AI Minister, a job market expert here to assist you with your inquiries. How can I help you today?",
                lang: "en",
                showIcons: false,
                chart: null,
                stopConversation: false
            }]);
        setErrorMessage("");
        setInlineStyles(prevState => ({
            ...prevState,
            containerHeight: "calc(100vh - 270px)"
        }));
        setIsSimulationCreated(false);
    }, [setConversationId, setTempRequestSearch]);

    const handleSelectChatMenuItem = useCallback((menuItem: ChatMenuItemProps) => {
        setSelectedChatMenuItemId(menuItem.id);
        handleNewConversation();
    }, [handleNewConversation]);

  const SubmitChat = async () => {
    setEnableSendBtn(false);
    setErrorMessage("");
    setInlineStyles(prevState => ({
      ...prevState,
      containerHeight: "calc(100vh - 270px)"
    }));
      const normalizedText = requestSearch.text.toLowerCase().trim();
      switch (selectedChatMenuItemId) {
          case LaborDemandDataItem:
              await callSQLLLM(normalizedText);
              break;

          case ForecastSimulationItem:
              await callChatWithSimulation(normalizedText);
              break;
      }
  }

  const callSQLLLM = async (normalizedText:string) => {
      if ([
            "what are the top occupations with the highest job growth over the next 10 years?",
            "what are the top occupations with highest job growth over next 10 years?",
            "what are the top occupations with highest job growth over the next ten years?",
            "what are the top occupations with highest job growth over next ten years?",
            "wat are the top occupations with the highest job growth over the next 10 years?",
            "wat r the top occupations with the highest job growth over the next 10 years?",
            "what are the top jobs with the highest job growth over the next 10 years?",
            "what are the top jobs with highest job growth over next 10 years?",
            "what r the top occupations with the highest job growth over the next 10 years?",
            "what r the top jobs with the highest job growth over the next 10 years?",
            "what r the top occupations with highest job growth over next 10 years?",
            "what r the top jobs with highest job growth over next 10 years?",
            "what are the top occupations with the highest job growth?",
            "what r the top occupations with the highest job growth?",
            "wat are the top occupations with the highest job growth?",
            "wat r the top occupations with the highest job growth?",
            "wat are the top jobs with the highest job growth?",
            "wat r the top jobs with the highest job growth?",
            "what are top occupations with highest job growth?",
            "what are top jobs with highest job growth?",
            "what r top occupations with highest job growth?",
            "what r top jobs with highest job growth?",
            "wat are top occupations with highest job growth?",
            "wat are top jobs with highest job growth?",
            "wat r top occupations with highest job growth?",
            "wat r top jobs with highest job growth?",
            "what are the top occupations for job growth over the next 10 years?",
            "what are the top occupations for job growth over next 10 years?",
            "what are the top occupations for job growth over the next ten years?",
            "what are the top occupations for job growth over next ten years?",
            "what are the top jobs for job growth over the next 10 years?",
            "what are the top jobs for job growth over next 10 years?",
            "what are the top jobs for job growth over the next ten years?",
            "what are the top jobs for job growth over next ten years?",
            "wat are the top occupations for job growth over the next 10 years?",
            "wat are the top jobs for job growth over the next 10 years?",
            "wat r the top occupations for job growth over the next 10 years?",
            "wat r the top jobs for job growth over the next 10 years?",
            "wat are the top occupations for job growth over next 10 years?",
            "wat are the top jobs for job growth over next 10 years?",
            "wat r the top occupations for job growth over next 10 years?",
            "wat r the top jobs for job growth over next 10 years?",
            "what are the occupations with the highest job growth over the next 10 years?",
            "what are the jobs with the highest job growth over the next 10 years?",
            "wat are the occupations with the highest job growth over the next 10 years?",
            "wat are the jobs with the highest job growth over the next 10 years?",
            "wat are the occupations with highest job growth over the next 10 years?",
            "wat are the jobs with highest job growth over the next 10 years?",
            "wat are the occupations with highest job growth over next 10 years?",
            "wat are the jobs with highest job growth over next 10 years?"
        ].includes(normalizedText)) {
            setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                const updatedTempRequestSearch = [...prevTempRequestSearch];
                const lastIndex = updatedTempRequestSearch.length - 1;

                if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                    updatedTempRequestSearch[lastIndex].placeholder = <p style={{ fontStyle: 'italic', color: '#003b5c66' }}>Running query {requestSearch.text} in {countryData.country}</p>;
                    updatedTempRequestSearch[lastIndex].loading = true;
                }
                return updatedTempRequestSearch;
            });

            setTimeout(() => {
                setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                    const updatedTempRequestSearch = [...prevTempRequestSearch];
                    const lastIndex = updatedTempRequestSearch.length - 1;

                    if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                        const formattedText = `<p>Top Occupations with Highest Job Growth Over the Next 10 Years in ${countryData.country}<p><ol>
              <li>
                  <strong>Home Health and Personal Care Aides</strong><br>
                  <strong>2022 Employment:</strong> 3,715,500<br>
                  <strong>2032 Employment:</strong> 4,520,100<br>
                  <strong>Job Growth:</strong> 804,600<br><br>
              </li>
              <li>
                  <strong>Software Developers</strong><br>
                  <strong>2022 Employment:</strong> 1,594,500<br>
                  <strong>2032 Employment:</strong> 2,004,900<br>
                  <strong>Job Growth:</strong> 410,400<br><br>
              </li>
              <li>
                  <strong>Cooks, Restaurant</strong><br>
                  <strong>2022 Employment:</strong> 1,361,200<br>
                  <strong>2032 Employment:</strong> 1,638,900<br>
                  <strong>Job Growth:</strong> 277,700<br><br>
              </li>
              <li>
                  <strong>Registered Nurses</strong><br>
                  <strong>2022 Employment:</strong> 3,172,500<br>
                  <strong>2032 Employment:</strong> 3,349,900<br>
                  <strong>Job Growth:</strong> 177,400<br><br>
              </li>
              <li>
                  <strong>Stockers and Order Fillers</strong><br>
                  <strong>2022 Employment:</strong> 2,851,600<br>
                  <strong>2032 Employment:</strong> 3,030,300<br>
                  <strong>Job Growth:</strong> 178,700<br><br>
              </li>
              <li>
                  <strong>Laborers and Freight, Stock, and Material Movers</strong><br>
                  <strong>2022 Employment:</strong> 2,988,500<br>
                  <strong>2032 Employment:</strong> 3,147,300<br>
                  <strong>Job Growth:</strong> 158,800<br><br>
              </li>
              <li>
                  <strong>Medical and Health Services Managers</strong><br>
                  <strong>2022 Employment:</strong> 509,500<br>
                  <strong>2032 Employment:</strong> 654,200<br>
                  <strong>Job Growth:</strong> 144,700<br><br>
              </li>
              <li>
                  <strong>General and Operations Managers</strong><br>
                  <strong>2022 Employment:</strong> 3,507,800<br>
                  <strong>2032 Employment:</strong> 3,655,100<br>
                  <strong>Job Growth:</strong> 147,300<br><br>
              </li>
              <li>
                  <strong>Light Truck Drivers</strong><br>
                  <strong>2022 Employment:</strong> 1,164,600<br>
                  <strong>2032 Employment:</strong> 1,298,400<br>
                  <strong>Job Growth:</strong> 133,800<br><br>
              </li>
              <li>
                  <strong>Financial Managers</strong><br>
                  <strong>2022 Employment:</strong> 792,600<br>
                  <strong>2032 Employment:</strong> 919,200<br>
                  <strong>Job Growth:</strong> 126,600
              </li>
            </ol><br/><br/><em>Source H2O</em>`;

                        updatedTempRequestSearch[lastIndex].text = formattedText;
                        updatedTempRequestSearch[lastIndex].placeholder = null;
                        requestSearch.generate_plot === "true"
                            ? (updatedTempRequestSearch[lastIndex].loading = true)
                            : (updatedTempRequestSearch[lastIndex].loading = false);
                    }
                    return updatedTempRequestSearch;
                });
            }, 3000);
            setTimeout(() => {
                setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                    const updatedTempRequestSearch = [...prevTempRequestSearch];
                    for (let i = updatedTempRequestSearch.length - 1; i >= 0; i--) {
                        if (updatedTempRequestSearch[i].name === "Whiteshield") {
                            updatedTempRequestSearch[i].chart = <PlotlyChart plotData={{
                                data: [
                                    {
                                        x: [
                                            'Home Health and Personal Care Aides',
                                            'Software Developers',
                                            'Cooks, Restaurant',
                                            'Registered Nurses',
                                            'Stockers and Order Fillers',
                                            'Laborers and Freight, Stock, and Material Movers',
                                            'Medical and Health Services Managers',
                                            'General and Operations Managers',
                                            'Light Truck Drivers',
                                            'Financial Managers'
                                        ],
                                        y: [804600, 410400, 277700, 177400, 178700, 158800, 144700, 147300, 133800, 126600],
                                        type: 'bar',
                                        marker: {
                                            color: 'rgba(169, 222, 245, 0.5)', // Blue color
                                            line: {
                                                color: 'rgba(169, 222, 245, 1)', // Blue border
                                                width: 1
                                            }
                                        }
                                    }


                                ], layout: {
                                    title: `Top Occupations with Highest Job Growth Over the Next 10 Years in ${countryData.country}`,
                                    xaxis: {
                                        title: 'Occupation',
                                        automargin: true
                                    },
                                    yaxis: {
                                        title: 'Job Growth',
                                        automargin: true
                                    },
                                    margin: {
                                        l: 50,
                                        r: 50,
                                        b: 100,
                                        t: 50,
                                        pad: 4
                                    }
                                }
                            }} />;
                            updatedTempRequestSearch[i].loading = false
                            break;
                        }
                    }

                    return updatedTempRequestSearch;
                });
            }, 24000);

        } else {
            try {
                const data = {
                    "q": requestSearch.text,
                    "conversation_id": conversationId,
                    "country": countryData.country,
                    "generate_plot": requestSearch.generate_plot
                };
                setRequestSearch(prevState => ({
                    ...prevState,
                    text: ""
                }));
                const config = {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                };
                const response = await axios.post('https://ws-llm-vm.eastus.cloudapp.azure.com:8097/sql_llm/query', data, config);

                setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                    const updatedTempRequestSearch = [...prevTempRequestSearch];
                    const lastIndex = updatedTempRequestSearch.length - 1;

                    if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                        updatedTempRequestSearch[lastIndex].placeholder = <p style={{ fontStyle: 'italic', color: '#003b5c66' }}>Running query {response.data.formatted_query}</p>;
                        updatedTempRequestSearch[lastIndex].loading = true;
                    }
                    return updatedTempRequestSearch;
                });
                while (true) {
                    if (tempRequestSearch && tempRequestSearch.length > 0 && tempRequestSearch[tempRequestSearch.length - 1].stopConversation) {
                        break;
                    } else {
                        const response2 = await axios.get(`https://ws-llm-vm.eastus.cloudapp.azure.com:8097/sql_llm/response?run_id=${response.data.run_id}`, config);
                        try {
                            if (response2.data && response2.data.hasOwnProperty("current_step")) {
                                setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                                    const updatedTempRequestSearch = [...prevTempRequestSearch];
                                    const lastIndex = updatedTempRequestSearch.length - 1;

                                    if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                                        updatedTempRequestSearch[lastIndex].placeholder = <p style={{ fontStyle: 'italic', color: '#003b5c66' }}>{response2.data.current_step}</p>;
                                    }
                                    return updatedTempRequestSearch;
                                });
                                continue;
                            } else if (response2.data && response2.data.hasOwnProperty("model_answer")) {
                                setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                                    const updatedTempRequestSearch = [...prevTempRequestSearch];
                                    const lastIndex = updatedTempRequestSearch.length - 1;

                                    if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                                        const formattedText = response2.data.model_answer
                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                            .replace(/\*(.*?)\*/g, '<i>$1</i>')
                                            .replace(/\n/g, "<br/>");

                                        updatedTempRequestSearch[lastIndex].text = formattedText;
                                        updatedTempRequestSearch[lastIndex].placeholder = null;
                                        requestSearch.generate_plot === "true" ? updatedTempRequestSearch[lastIndex].loading = true : updatedTempRequestSearch[lastIndex].loading = false;
                                    }
                                    return updatedTempRequestSearch;
                                });
                                break; // Exit the loop if the plot status is no longer processing
                            }
                        } catch (error: any) {
                            if (error.response && error.response.data && error.response.data.error_message) {
                                setErrorMessage(error.response.data.error_message);
                                setInlineStyles(prevState => ({
                                    ...prevState,
                                    containerHeight: "calc(100vh - 320px)"
                                }));
                                break; // Exit the loop if the plot status is no longer processing
                            }
                        }
                    }
                }
                if (tempRequestSearch) {
                    setTimeout(() => {
                        setTempRequestSearch && setTempRequestSearch(prevState => (
                            prevState.map(item => ({
                                ...item,
                                showIcons: true
                            }))
                        ));
                    }, tempRequestSearch[tempRequestSearch.length - 1].text.length * 15);
                }
                if (requestSearch.generate_plot === "true") {
                    try {
                        while (true) {
                            const responseOfGetReq1 = await axios.get(`https://ws-llm-vm.eastus.cloudapp.azure.com:8097/sql_llm/plot_status?plot_id=${response.data.plot_id}`, config);
                            if (responseOfGetReq1.data && responseOfGetReq1.data.plot_status === "processing") {
                                continue;
                            }
                            try {
                                let responseOfGetReq2: { data: { data: any, layout: any } } = await axios.get(`https://ws-llm-vm.eastus.cloudapp.azure.com:8097/sql_llm/plot?plot_id=${response.data.plot_id}`, config);

                                setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                                    const updatedTempRequestSearch = [...prevTempRequestSearch];
                                    for (let i = updatedTempRequestSearch.length - 1; i >= 0; i--) {
                                        if (updatedTempRequestSearch[i].name === "Whiteshield") {
                                            updatedTempRequestSearch[i].chart = <PlotlyChart plotData={responseOfGetReq2.data} />;
                                            updatedTempRequestSearch[i].loading = false
                                            break;
                                        }
                                    }

                                    return updatedTempRequestSearch;
                                });
                            } catch (error: any) {
                                // setErrorMessage
                                if (error.response.data.error_message !== "No data to plot.") {
                                    setErrorMessage(error.response.data.error_message);
                                    setInlineStyles(prevState => ({
                                        ...prevState,
                                        containerHeight: "calc(100vh - 320px)"
                                    }));
                                }
                            }
                            break; // Exit the loop if the plot status is no longer processing

                        }
                    } catch (error) {
                        // setErrorMessage
                        console.log('Error:', error);
                    }
                }
                setLoadingResponse(false)
                setTempRequestSearch && setTempRequestSearch(prevState => (
                    prevState.map(item => ({
                        ...item,
                        loading: false
                    }))
                ));




                setRequestSearch({ text: "", generate_plot: "true" })

            } catch (error) {
                console.error('Error:', error);
            }
        }
  };

  const callChatWithSimulation = async (normalizedText:string) => {
      try {
          setRequestSearch(prevState => ({
              ...prevState,
              text: ""
          }));

          const CancelTokenAddToConversation = axios.CancelToken;
          const sourceAddToConversation = CancelTokenAddToConversation.source();
          setAddToConversationToken(sourceAddToConversation);

          const [errorAddToConversation, responseAddToConversation] = await url.addToConversation(conversationId!, normalizedText, sourceAddToConversation.token);
          if (errorAddToConversation || (responseAddToConversation && responseAddToConversation[0] && responseAddToConversation[0].error)) {
              setErrorMessage(errorAddToConversation || responseAddToConversation[0].error);
              setInlineStyles(prevState => ({
                  ...prevState,
                  containerHeight: "calc(100vh - 320px)"
              }));
              return;
          }

          setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
              const updatedTempRequestSearch = [...prevTempRequestSearch];
              const lastIndex = updatedTempRequestSearch.length - 1;

              if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                  if (!isSimulationCreated) {
                      updatedTempRequestSearch[lastIndex].placeholder = <p style={{ fontStyle: 'italic', color: '#003b5c66', border: '1px solid white', padding: '10px', borderRadius: '20px' }}>{responseAddToConversation.content}</p>;
                      updatedTempRequestSearch[lastIndex].loading = true;
                  }
                  else {
                      const formattedText = responseAddToConversation.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<i>$1</i>')
                          .replace(/\n/g, "<br/>");

                      updatedTempRequestSearch[lastIndex].text = formattedText;
                      updatedTempRequestSearch[lastIndex].showIcons = true;
                  }
              }
              return updatedTempRequestSearch;
          });

          if (!isSimulationCreated) {
              const CancelTokenGenerateScenario = axios.CancelToken;
              const sourceGenerateScenario = CancelTokenGenerateScenario.source();
              setGenerateScenarioToken(sourceGenerateScenario);

              const [errorGenerateScenario, responseGenerateScenario] = await url.generateScenario(conversationId!, sourceGenerateScenario.token);
              if (errorGenerateScenario || (responseGenerateScenario && responseGenerateScenario[0] && responseGenerateScenario[0].error)) {
                  setErrorMessage(errorGenerateScenario || responseGenerateScenario[0].error);
                  setInlineStyles(prevState => ({
                      ...prevState,
                      containerHeight: "calc(100vh - 320px)"
                  }));
                  return;
              }

              setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                  const updatedTempRequestSearch = [...prevTempRequestSearch];
                  const lastIndex = updatedTempRequestSearch.length - 1;

                  if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                      updatedTempRequestSearch[lastIndex].placeholder = <>{updatedTempRequestSearch[lastIndex].placeholder}<p style={{ fontStyle: 'italic', color: '#003b5c66', border: '1px solid white', padding: '10px', borderRadius: '20px' }}>{responseGenerateScenario.content}</p></>;
                      updatedTempRequestSearch[lastIndex].loading = true;
                  }
                  return updatedTempRequestSearch;
              });

              const CancelTokenGetPlotsResults = axios.CancelToken;
              const sourceGetPlotsResults = CancelTokenGetPlotsResults.source();
              setGetPlotsResultsToken(sourceGetPlotsResults);

              var [errorGetPlotsResults, responseGetPlotsResults] = await url.getPlotsResults(conversationId!, sourceGetPlotsResults.token);
              if (errorGetPlotsResults || (responseGetPlotsResults && responseGetPlotsResults[0] && responseGetPlotsResults[0].error)) {
                  setErrorMessage(errorGetPlotsResults || responseGetPlotsResults[0].error);
                  setInlineStyles(prevState => ({
                      ...prevState,
                      containerHeight: "calc(100vh - 320px)"
                  }));
                  return;
              }

              setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
                  const updatedTempRequestSearch = [...prevTempRequestSearch];
                  const lastIndex = updatedTempRequestSearch.length - 1;
                  if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
                      if (responseGetPlotsResults && responseGetPlotsResults.content) {
                          let graphs = responseGetPlotsResults.content.map((plot: any) => {
                              return JSON.parse(plot);
                          });
                          updatedTempRequestSearch[lastIndex].chart = <div className="row">{
                              graphs.map((graph: any, index: number) => {
                                  return <div key={"graph-" + index} className="col-md-6">
                                      <PlotlyChart plotData={graph} />
                                  </div>
                              })
                          }</div>;
                      }
                      updatedTempRequestSearch[lastIndex].loading = false
                  }
                  return updatedTempRequestSearch;
              });

          }

          // A flag to determine that the very first call for the add to conversation is done and that the simulation has been created
          // so the next time when we call callChatWithSimulation, it will only call addToConversation
          setIsSimulationCreated(true);

          setLoadingResponse(false)
          setTempRequestSearch && setTempRequestSearch(prevState => (
              prevState.map(item => ({
                  ...item,
                  loading: false
              }))
          ));
      } catch (error) {
          console.error('Error:', error);
      }
  };

  useEffect(() => {
    if (requestSearch.text.length === 0) {
      setEnableSendBtn(false);
    } else {
      setEnableSendBtn(true);
    }
  }, [requestSearch]);

  useEffect(() => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
      if (isNearBottom) {
        chatContainerRef.current.scrollTop = scrollHeight;
      }
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  });


  const copyTextToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        console.log('Text copied to clipboard:', text);
        // You can add any additional logic here after copying the text
      })
      .catch(error => {
        console.error('Error copying text to clipboard:', error);
      });
  };

  const replaceCorruptedHTMLTags = useCallback((translatedText: string) => {
    return translatedText.replaceAll("</ ب>", "</b>").replaceAll("< ب>", "<b>").replaceAll("< /b>", "</b>").replaceAll("< b>", "<b>").replaceAll("< br>", "<br>").replaceAll("< /br>", "</br>");
  }, []);


  const translateResponseToArabic = async (translatedText: string, index: number) => {
    if (tempRequestSearch) {
      const updatedTempRequestSearch = [...tempRequestSearch];
      updatedTempRequestSearch[index] = {
        ...updatedTempRequestSearch[index],
        lang: updatedTempRequestSearch[index].lang === "ar" ? "en" : "ar"
      };
      if (!updatedTempRequestSearch[index].textar || updatedTempRequestSearch[index].textar === "") {
        const [errTranslatedText, incomingTranslatedText] = await getTranslation(translatedText);
        var tempIncomingTranslatedText = replaceCorruptedHTMLTags(incomingTranslatedText);
        if (errTranslatedText) {
          console.warn(errTranslatedText);
        }
        else {
          updatedTempRequestSearch[index] = {
            ...updatedTempRequestSearch[index],
            textar: tempIncomingTranslatedText
          };
        }
      }
      setTempRequestSearch && setTempRequestSearch(updatedTempRequestSearch);
    }
  }

  const handleFeedbackReaction = (reaction: string, index: number) => {
    if (tempRequestSearch) {
      const updatedTempRequestSearch = tempRequestSearch && [...tempRequestSearch];
      updatedTempRequestSearch[index] = {
        ...updatedTempRequestSearch[index],
        feedback: reaction
      };
      setTempRequestSearch && setTempRequestSearch(updatedTempRequestSearch);
    }
  };
    const HandleStopConversation = () => {
        if (selectedChatMenuItemId === ForecastSimulationItem) {
            if (addToConversationToken)
                addToConversationToken.cancel();

            if (generateScenarioToken)
                generateScenarioToken.cancel();

            if (getPlotsResultsToken)
                getPlotsResultsToken.cancel();
        }

    setTempRequestSearch && setTempRequestSearch(prevTempRequestSearch => {
      const updatedTempRequestSearch = [...prevTempRequestSearch];
      const lastIndex = updatedTempRequestSearch.length - 1;

      if (lastIndex >= 0 && updatedTempRequestSearch[lastIndex].name === "Whiteshield") {
        updatedTempRequestSearch[lastIndex].stopConversation = true;
      }
      return updatedTempRequestSearch;
    });
        setLoadingResponse(false)
        setErrorMessage("");
        setInlineStyles(prevState => ({
            ...prevState,
            containerHeight: "calc(100vh - 270px)"
        }));
  }

  return (
    <Modal opened={isOpen} onClose={onClose} style={{ minWidth: "75vw", height: "100vh" }} crossButton={true} modalClass={`chatbotModal`} isSticky>
      <div className="d-flex justify-content-between py-2">
        <div></div>
        <img src={AIMinisterIconTop} alt='AI Minister' width={100} />
        <div className="modal__close-buttom" onClick={onClose}>
          <FaTimes />
        </div>
      </div>
      <div id="searchContainerWrapper" className="rounded-3" style={{ background: "#fff" }}>

        <div className="p-3 overflow-auto chat-container" ref={chatContainerRef} style={{ height: inlineStyles.containerHeight }}>
          {tempRequestSearch && tempRequestSearch.length > 1 ? null : <div className="d-flex align-items-center" style={{ minHeight: "calc(100% - 150px)" }}>

            <div className="d-flex justify-content-around pb-5">
              {
                questionsInfo.map((e: questionsInfoType, i: number) => <div className="card mx-3 pe-auto" key={i} style={{ width: "23%" }} onClick={() => handleSuggestedBoxes(e.question, e.generateGraph)}>
                  <div className="card-body">
                    <h5 className="card-title">{e.title}</h5>
                    <h6 className="card-subtitle mb-2 text-muted">{e.subtitle}</h6>
                    <p className="card-text">{e.question}</p>
                  </div>
                </div>)
              }
            </div>
          </div>}


          {tempRequestSearch && tempRequestSearch.map((e: any, i: number) => {
            return <div className={`${e.name === "Whiteshield" ? "flex-row" : "flex-row-reverse"} d-flex py-3`} key={i}>
                <div className="p-1">{e.name === "Whiteshield" ? <img src={AIMinisterIcon} alt="AI Minister" width={35} height={45} /> : <FaUser size={20} />}</div>
                {e.name === "Whiteshield" ? <div className={`chat mx-2 py-3 ${selectedChatMenuItemId === ForecastSimulationItem ? "px-3" : "px-4"} position-relative ${selectedChatMenuItemId === ForecastSimulationItem && e.chart && e.stopConversation === false && " w-100"}`}>
                {(e.placeholder !== null && e.stopConversation === false) ? e.placeholder : null}
                {e.text && e.lang === "en" ? <Typewriter text={e.text} delay={5} dir="ltr" stop={e.stopConversation} setLoadingResponse={setLoadingResponse} /> : null}
                {e.textar && e.lang === "ar" ? <Typewriter text={e.textar} delay={5} dir="rtl" setLoadingResponse={setLoadingResponse} /> : null}
                    {(e.chart && e.stopConversation === false) ? <div className={`pt-3 ${e.loading && "pb-3"} ${selectedChatMenuItemId === ForecastSimulationItem && e.chart && e.stopConversation === false ? " d-block w-100 " : " d-flex justify-content-center "}`}>{e.chart}</div> : <div></div>}
                {(e.loading && e.stopConversation === false) ? <div className="dotsLoader my-3"></div> : null}
                {e.showIcons ? <div className="d-flex position-absolute" style={{ bottom: -32, right: 15 }}>
                  <IconButton onClick={() => handleFeedbackReaction("dislike", i)}><ThumbDown sx={{ fontSize: 15 }} color={`${e.feedback === "dislike" ? "error" : "action"}`} /></IconButton>
                  <IconButton onClick={() => handleFeedbackReaction("like", i)}><ThumbUp sx={{ fontSize: 15 }} color={`${e.feedback === "like" ? "success" : "action"}`} /></IconButton>
                  <IconButton onClick={() => translateResponseToArabic(e.text, i)}><Translate sx={{ fontSize: 15 }} /></IconButton>
                  <IconButton onClick={() => copyTextToClipboard(e.lang === "ar" ? e.textar : e.text)}><ContentCopy sx={{ fontSize: 15 }} /></IconButton>
                </div> : null}
              </div> : <div className={`bg-white mx-2 py-3 px-4`}>
                {e.text}
              </div>}
            </div>
          }
          )}
        </div>
        <div className="chat-actions">
          <div className="chat-actions__container p-3">
            <span className="input-group requiredField" id="search_controls">
              <TextField
                multiline
                maxRows={8}
                variant="filled"
                placeholder="Message AI Minister"
                name="search"
                id="search"
                value={requestSearch.text}
                onChange={handleChangeRequestSearch}
                onKeyDown={handleKeyDownOnSearch}
                className="form-control"
                InputProps={{
                    startAdornment: <ChatMenu handleSelectChatMenuItem={handleSelectChatMenuItem} />,
                }}
              />
              <div className="chat-actions__sendbtn rounded-circle d-flex justify-content-center align-items-center ms-1">
                {!loadingResponse ?
                  <Send id="search_submit" onClick={enableSendBtn ? handleSubmitSearch : undefined} color={(enableSendBtn ? "primary" : "disabled")} /> :
                  <Stop onClick={HandleStopConversation} />
                }
              </div>
            </span>
            <div className="d-flex justify-content-end mt-1">
              {tempRequestSearch && tempRequestSearch.length ? <div className="d-flex align-items-center">

                {/* <div className="d-flex align-items-center">
                  <button className="btn btn-light" onClick={() => handleFontSize(true)}>A+</button>|<button className="btn btn-light" onClick={() => handleFontSize(false)}>A-</button>
                </div>
                <MaterialUISwitch sx={{ m: 1 }} onClick={() => SetMode(mode === "dark" ? "light" : "dark")} /> */}
                <Button className="btn btn-light text-capitalize" onClick={handleNewConversation}>New Conversation</Button>
              </div> : null}
            </div>
            {errorMessage !== "" ? <div className="alert alert-danger d-flex align-items-center" role="alert">
              <Warning fontSize="small" style={{ width: 20 }} />
              <div> &nbsp; {errorMessage}</div>
            </div> : null}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ChatModal;
